/**
 * Frontend mirror of `rules/transaction_ai_tier_overrides.yaml`
 * (M30.5.2). The backend's `hr_operations.core.tier_resolver`
 * is the canonical source — this module hard-codes the same
 * matrix so the frontend can render Sonnet/Haiku tier badges
 * inline without a backend round-trip.
 *
 * SAFETY: the values here MUST stay in sync with the YAML. The
 * vitest `ai-tier-registry.test.ts` pins the matrix; if the
 * backend YAML moves, that test fails until the constants below
 * are updated.
 *
 * Why frontend-side mirror instead of an API call:
 *   - The tier is rendered as chrome on every AI assist panel —
 *     a round-trip per render is silly.
 *   - The YAML is checked into the repo + ships with the code,
 *     so frontend "sees" the same data automatically.
 *   - The matrix is small (5 panels × 7 types = 35 cells, with
 *     1 EOS override block on top of a default row).
 */

export const TRANSACTION_TYPES = [
  "leave",
  "loan",
  "excuse",
  "attendance",
  "overtime",
  "eos",
  "contract_amendment",
] as const;

export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const PANELS = [
  "validation",
  "cross_reference",
  "suggestions",
  "completeness",
  "auto_fill",
] as const;

export type Panel = (typeof PANELS)[number];

export type Tier = "sonnet" | "haiku";

interface TierMatrix {
  default: Record<Panel, Tier>;
  overrides: Partial<Record<TransactionType, Record<Panel, Tier>>>;
}

const TIER_MATRIX: TierMatrix = {
  default: {
    validation: "sonnet",
    cross_reference: "sonnet",
    suggestions: "haiku",
    completeness: "haiku",
    auto_fill: "haiku",
  },
  overrides: {
    // EOS — every panel goes Sonnet (M30 F2 lock). Highest-stakes
    // transaction; the cost delta is irrelevant against the risk
    // of a wrong auto-fill on a termination type.
    eos: {
      validation: "sonnet",
      cross_reference: "sonnet",
      suggestions: "sonnet",
      completeness: "sonnet",
      auto_fill: "sonnet",
    },
  },
};

export function resolveTier(
  transactionType: TransactionType,
  panel: Panel,
): Tier {
  const override = TIER_MATRIX.overrides[transactionType];
  if (override && panel in override) {
    return override[panel];
  }
  return TIER_MATRIX.default[panel];
}

/** Returns every panel for a given transaction type with its
 *  resolved tier. Useful for rendering the panel set in order. */
export function tiersForTransaction(
  transactionType: TransactionType,
): Array<{ panel: Panel; tier: Tier }> {
  return PANELS.map((panel) => ({
    panel,
    tier: resolveTier(transactionType, panel),
  }));
}
